/**
 * Wave 6 — Award reconciliation UX helpers (mirrors BE classifier for display).
 * Authority remains POST …/reconcile + createContractFromAward.
 */

/** Keep in sync with backend `MATERIAL_MISMATCH_PCT`. */
export const MATERIAL_MISMATCH_PCT = 5;

export type ReconciliationResult =
  | 'PENDING'
  | 'MATCHED'
  | 'MINOR_VARIANCE'
  | 'MATERIAL_MISMATCH';

export type ReconciliationView = {
  result: ReconciliationResult;
  variancePct: number | null;
  varianceAmount: number | null;
  proposalValue: number | null;
  awardValue: number | null;
  allowsContract: boolean;
  badgeColor: 'default' | 'success' | 'warning' | 'error' | 'processing';
  summary: string;
};

export function reconciliationAllowsContract(result: string): boolean {
  return result === 'MATCHED' || result === 'MINOR_VARIANCE';
}

export function reconciliationBadgeColor(
  result: string
): ReconciliationView['badgeColor'] {
  switch (result) {
    case 'MATCHED':
      return 'success';
    case 'MINOR_VARIANCE':
      return 'warning';
    case 'MATERIAL_MISMATCH':
      return 'error';
    case 'PENDING':
      return 'processing';
    default:
      return 'default';
  }
}

export function computeAwardReconciliationPreview(
  proposalValue: number,
  awardValue: number
): Pick<ReconciliationView, 'result' | 'variancePct' | 'varianceAmount' | 'allowsContract'> {
  const varianceAmount = awardValue - proposalValue;
  const variancePct =
    proposalValue === 0
      ? awardValue === 0
        ? 0
        : 100
      : (Math.abs(varianceAmount) / Math.abs(proposalValue)) * 100;
  let result: ReconciliationResult = 'MATCHED';
  if (variancePct === 0) result = 'MATCHED';
  else if (variancePct <= MATERIAL_MISMATCH_PCT) result = 'MINOR_VARIANCE';
  else result = 'MATERIAL_MISMATCH';
  return {
    result,
    variancePct,
    varianceAmount,
    allowsContract: reconciliationAllowsContract(result),
  };
}

export function buildReconciliationView(
  row: Record<string, unknown> | null | undefined
): ReconciliationView {
  if (!row) {
    return {
      result: 'PENDING',
      variancePct: null,
      varianceAmount: null,
      proposalValue: null,
      awardValue: null,
      allowsContract: false,
      badgeColor: 'processing',
      summary: 'Reconciliation not computed yet — run Reconcile against linked proposal',
    };
  }
  const result = String(row.result || 'PENDING') as ReconciliationResult;
  const variancePct =
    row.variance_pct != null || row.variancePct != null
      ? Number(row.variance_pct ?? row.variancePct)
      : null;
  const varianceAmount =
    row.variance_amount != null || row.varianceAmount != null
      ? Number(row.variance_amount ?? row.varianceAmount)
      : null;
  const proposalValue =
    row.proposal_value != null || row.proposalValue != null
      ? Number(row.proposal_value ?? row.proposalValue)
      : null;
  const awardValue =
    row.award_value != null || row.awardValue != null
      ? Number(row.award_value ?? row.awardValue)
      : null;

  let summary = `Result: ${result}`;
  if (result === 'MATERIAL_MISMATCH') {
    summary = `MATERIAL_MISMATCH — abs variance exceeds ${MATERIAL_MISMATCH_PCT}% of proposal; contract conversion blocked`;
  } else if (result === 'MINOR_VARIANCE') {
    summary = `MINOR_VARIANCE — within ${MATERIAL_MISMATCH_PCT}% threshold; contract conversion allowed`;
  } else if (result === 'MATCHED') {
    summary = 'MATCHED — award aligns with proposal; contract conversion allowed';
  }

  return {
    result,
    variancePct: variancePct != null && !Number.isNaN(variancePct) ? variancePct : null,
    varianceAmount:
      varianceAmount != null && !Number.isNaN(varianceAmount) ? varianceAmount : null,
    proposalValue: proposalValue != null && !Number.isNaN(proposalValue) ? proposalValue : null,
    awardValue: awardValue != null && !Number.isNaN(awardValue) ? awardValue : null,
    allowsContract: reconciliationAllowsContract(result),
    badgeColor: reconciliationBadgeColor(result),
    summary,
  };
}

export type ContractReviewContextView = {
  linked: boolean;
  contractReviewId: string | null;
  path: string | null;
  note: string;
};

export function buildContractReviewContext(
  row: Record<string, unknown>
): ContractReviewContextView {
  const id = String(row.contract_review_id || row.contractReviewId || '').trim();
  if (!id) {
    return {
      linked: false,
      contractReviewId: null,
      path: null,
      note: 'No Contract Review linked — CR remains governance (Approval Runtime), optional on award/contract',
    };
  }
  return {
    linked: true,
    contractReviewId: id,
    path: `/growth/contract-reviews/${encodeURIComponent(id)}`,
    note: 'Contract Review is governance context — not replaced by EPC Contract / CA workspace',
  };
}
